import { Package } from 'lucide-react';
import { colors } from '../constants/colors';

interface ShowEndedOverlayProps {
  totalSales: number;
  orderCount: number;
  peakViewerCount: number;
  onGoToDashboard: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20,
  background: 'rgba(0,0,0,0.3)',
};

const cardStyle: React.CSSProperties = {
  background: colors.white,
  border: `1px solid ${colors.gray[200]}`,
  borderRadius: 18,
  padding: 32,
  maxWidth: 460,
  width: '100%',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  boxShadow: '0 4px 24px rgba(28,32,51,0.06)',
};

const eyebrowStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: colors.gray[600],
};

const titleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  color: colors.ink.black,
  letterSpacing: '-0.01em',
  margin: 0,
};

const descriptionStyle: React.CSSProperties = {
  fontSize: 14,
  color: colors.gray[600],
  lineHeight: 1.5,
  margin: 0,
};

const statsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: 4,
  padding: '18px 0',
  borderTop: `1px solid ${colors.gray[200]}`,
  borderBottom: `1px solid ${colors.gray[200]}`,
};

const statValueStyle: React.CSSProperties = {
  fontSize: 26,
  fontWeight: 700,
  lineHeight: 1.2,
};

const statLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: colors.gray[600],
  marginTop: 4,
};

const ctaButtonStyle: React.CSSProperties = {
  background: colors.accent.purple,
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  padding: '14px 20px',
  borderRadius: 12,
  border: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  cursor: 'pointer',
  alignSelf: 'center',
};

export function ShowEndedOverlay({
  totalSales,
  orderCount,
  peakViewerCount,
  onGoToDashboard,
}: ShowEndedOverlayProps) {
  const formattedSales = `$${totalSales.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <span style={eyebrowStyle}>Show Ended</span>
        <h2 style={titleStyle}>Nice show!</h2>
        <p style={descriptionStyle}>
          Sales are locked in. Head to your seller dashboard to start fulfillment, print labels, and manage refunds.
        </p>
        <div style={statsGridStyle}>
          <div>
            <div style={{ ...statValueStyle, color: colors.status.success }}>{formattedSales}</div>
            <div style={statLabelStyle}>Gross Sales</div>
          </div>
          <div>
            <div style={{ ...statValueStyle, color: colors.ink.black }}>{orderCount}</div>
            <div style={statLabelStyle}>Orders</div>
          </div>
          <div>
            <div style={{ ...statValueStyle, color: colors.ink.black }}>{peakViewerCount}</div>
            <div style={statLabelStyle}>Peak Viewers</div>
          </div>
        </div>
        <button style={ctaButtonStyle} onClick={onGoToDashboard}>
          <Package size={16} />
          Go to Seller Dashboard
        </button>
      </div>
    </div>
  );
}
